import { Package } from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { formatCurrency } from '@/lib/utils';
import type { OrderItem } from './types';

interface OrderItemsCardProps {
  items: OrderItem[];
  orderStatus: string;
  onEditItems: () => void;
  onFulfillItems: () => void;
}

export default function OrderItemsCard({ items, orderStatus, onEditItems, onFulfillItems }: OrderItemsCardProps) {
  const canFulfill = orderStatus === 'pending' || orderStatus === 'processing';

  return (
    <Card padding="none">
      <div className="flex items-center justify-between px-5 pt-5 mb-4">
        <h3 className="text-lg font-semibold text-stone-900">Unfulfilled Items</h3>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onEditItems}>
            Edit Items
          </Button>
          {canFulfill && (
            <Button variant="primary" size="sm" onClick={onFulfillItems}>
              Fulfill Items
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-t border-b border-stone-200 text-left text-stone-500">
              <th className="px-5 py-3 font-medium">Product</th>
              <th className="px-5 py-3 font-medium">SKU</th>
              <th className="px-5 py-3 font-medium text-right">Price</th>
              <th className="px-5 py-3 font-medium text-right">Qty</th>
              <th className="px-5 py-3 font-medium text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-stone-100 last:border-0">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.product_name}
                        className="h-10 w-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-stone-100">
                        <Package className="h-5 w-5 text-stone-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-stone-900">{item.product_name}</p>
                      {item.variant_name && (
                        <p className="text-xs text-stone-500">{item.variant_name}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-stone-500">{item.sku}</td>
                <td className="px-5 py-3 text-right text-stone-700">{formatCurrency(item.unit_price)}</td>
                <td className="px-5 py-3 text-right text-stone-700">{item.quantity}</td>
                <td className="px-5 py-3 text-right font-medium text-stone-900">{formatCurrency(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
